import React, { useRef, useState, useEffect, forwardRef } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { FaArrowCircleRight, FaExclamationTriangle, FaPlus, FaSave, FaTimes, FaTrash } from 'react-icons/fa';
import { API_URL_All } from '../../api';
import { toast } from "react-toastify";
import * as general from '../../libs/general';
import { RankDiscountDetails } from './RankDiscountInfo';
import { CheckingTicket, FeedbackChecking } from '../tickets/Tickets';
import axiosInstance from '../../axiosInstance';
import { DEFAULT_PRICE_TYPE_NAME, resolveDefaultPriceTypeName } from '../../libs/contact-price-type';
import { fetchDefaultCustomerPriceTypeName } from '../../libs/customer-price-type';
import { resolveOrderProductId } from '../../libs/order-product-picker';
import { validateMuaHoSupplierBeforeSave } from '../../libs/customerCreatePermission';
import OrderProductPickerModal from './OrderProductPickerModal';
import RecordCreatedMeta from '../RecordCreatedMeta';

const buildProductImageUrl = (path) => {
  if (!path) return '';
  const value = String(path);
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  return `${API_URL_All}/${value}`;
};

/** Trạng thái đối soát "Vào phiếu" (CommonConstant.MD_VAO_PHIEU). */
const ORDER_STATUS_RATE_VAO_PHIEU = 14;

const OrderEditor = ({ data, handleSearch }) => {
  const readOnly = Boolean(data?.readOnly);
  const showOrderSupplierSection = data?.showOrderSupplierSection === true;
  const muaHoSupplierReadOnly = Boolean(data?.muaHoSupplierReadOnly);
  const requireMuaHoSupplier = Boolean(data?.requireMuaHoSupplier);
  /** Mặc định true (màn user); admin truyền theo quyền Đơn thường. */
  const canDeleteOrder = data?.canDeleteOrder !== false;
  const [show, setShow] = useState(true);
  const [isLocked, setIsLocked] = useState(true);
  /** Chỉ xem (không Sửa): khóa toàn bộ ô nhập dù đơn chưa locked trên server. */
  const inputDisabled = readOnly || isLocked;
  const supplierDisabled = inputDisabled || muaHoSupplierReadOnly;
  const [model, setModel] = useState({});
  /** Tạo mới: cờ từ nút Mua hộ. Sửa đơn: đơn mua hộ là đơn đã có nhà cung cấp. */
  const isMuaHoOrder = requireMuaHoSupplier || model.providerId != null;
  const [customerCode, setCustomerCode] = useState('');
  /** Cảnh báo đơn đã vào phiếu đối soát bị lệch số tiền so với lúc tạo phiếu (từ backend). */
  const [reconcileWarning, setReconcileWarning] = useState('');
  const [productPickerModal, setProductPickerModal] = useState({
    show: false,
    detailIndex: -1,
    keyword: '',
    items: [],
    previewProductId: null,
    previewProductDetail: null,
    loadingList: false,
    loadingPreview: false,
    allowMultiple: false,
  });
  const productDetailCacheRef = useRef(new Map());
  const productPreviewRequestRef = useRef(0);
  const productSearchRequestRef = useRef(0);

  useEffect(() => {
    fetchOneRecord(data.id);
  }, [data]);

  const setSizeItem = async (name, e) => {
    var value = e.target.value;
    const newModel = { ...model };
    if (name === 'packageUnitM3') {
      newModel.sizeX = 0;
      newModel.sizeY = 0;
      newModel.sizeZ = 0;
    }

    newModel[name] = value;
    setModel(newModel);
  }


  const addOrderProduct = () => {
    if (readOnly || model.locked) return;
    setModel({ ...model, details: [...(model.details ?? []), {}] });
  };

  const handleRemoveItem = (indexToRemove) => {
    if (readOnly) return;
    general.openConfirm(async () => {
      const newDetails = model.details.filter((_, i) => i !== indexToRemove);
      setModel({ ...model, details: newDetails });
    });
  };
  const handleDeletOrder = (e) => {
    if (!canDeleteOrder) {
      toast.warning("Bạn không có quyền xóa đơn hàng.");
      return;
    }
    if (!readOnly && isLocked) return;
    general.openConfirm(async () => {
      try {
        var modelData = {};
        var requestApi = `/api/import-orders/deleteOrder/${model.id}`;
        const response = await axiosInstance.put(requestApi, modelData);

        if (!response.data.success) {
          toast.error(response.data.message || "Không thể xóa đơn hàng.");
          return;
        }
        toast.success(response.data.message || "Đã xóa.");
        if (handleSearch) handleSearch();
        handleModalClose();

      } catch (error) {
        console.error(error);
        toast.error(error.message);
      }
    })
  };

  const handleTransformOrder = (e) => {
    if (readOnly) return;
    general.openConfirm(async () => {
      try {
        var modelData = {};
        var requestApi = `/api/import-orders/transformOrder/${model.id}`;
        const response = await axiosInstance.put(requestApi, modelData);

        if (!response.data.success) {
          toast.success(response.data.message);
          console.log(response.data);
          return;
        }
        console.log(response);
        toast.success(response.data.message);
        if (handleSearch) handleSearch();
        handleModalClose();

      } catch (error) {
        console.error(error);
        toast.error(error.message);
      }

    },"Bạn có chắc chắn muốn chuyển bản nháp này thành đơn hàng chuẩn không?");
  };

  const handleModalClose = (e) => {
    document.querySelectorAll('.show .error-msg').forEach((item) => {
      if (item.parentNode) {
        item.parentNode.removeChild(item);
      }
    });
    document.activeElement.blur();
    setShow(false);
  };

  const handleSubmitForm = async(e) => {
    if (readOnly) return;
    e.preventDefault();
    var errorTag = e.currentTarget.querySelector('.error-msg');
    if (errorTag) return;

    const supplierErr = validateMuaHoSupplierBeforeSave({
      requireMuaHoSupplier,
      model,
      vatMode: false,
    });
    if (supplierErr) {
      general.showFormError('.show .modal-body', supplierErr);
      return;
    }

    const { customerProductList: _omitProducts, ...orderFields } = model;
    var dataModel = {
      ...orderFields,
      requireProvider: requireMuaHoSupplier || undefined,
      details: (model.details ?? []).map((item) => {
        const { properties, parentProductSnapshot, ...detail } = item;
        return {
          ...detail,
          productPropertyId: detail.productPropertyId ? Number(detail.productPropertyId) : null,
        };
      }),
    };

    try {
      var updateOrderApi = `/api/import-orders/saveOrder`;
      const response = await axiosInstance.put(updateOrderApi, dataModel);
      if (!response.data.success) {
        general.showFormError('.show .modal-body', response.data);
        console.log(response.data);
        return;
      }
      console.log(response);
      toast.success(response.data.message);
      if (response.data.warning) {
        toast.warning(response.data.warning, { autoClose: 15000 });
      }
      if (handleSearch) handleSearch();
      handleModalClose();
      window.dispatchEvent(new Event('refresh-operation-info'));
    } catch (error) {
      toast.error(error.message);
    }

  };

  const fetchOneRecord = async (orderId) => {
    document.querySelectorAll('.error-msg').forEach((item) => {
      if (item.parentNode) {
        item.parentNode.removeChild(item);
      }
    });
    const isNumber = Number.isFinite(orderId);
    if (!isNumber) {
      orderId = 0;
    }
    setModel({});
    setReconcileWarning('');
    try {
      var urlApi = `/api/import-orders/getOrder?id=${orderId}`;
      const response = await axiosInstance.get(urlApi);
      setReconcileWarning(response.data?.warning || '');
      if (response.data?.success === false) {
        toast.error(response.data?.message || 'Không có quyền xem đơn hàng.');
        setShow(false);
        return;
      }
      var obj = response.data.data;
      // if (!obj.details) obj.details = [{}];
      var checkSize = obj.sizeX + obj.sizeY + obj.sizeZ;
      obj.size3 = (checkSize > 0);
      if (obj.details) {
        obj.details.forEach((item, index) => {
          var checkSize = item.sizeX + item.sizeY + item.sizeZ;
          item.size3 = (checkSize > 0);
        });
      }

      if (!obj.id && data.isDraft) obj.isDraft = true;
      if (!obj.id && !requireMuaHoSupplier) {
        obj.details = [];
      }

      obj = await enrichModelWithCustomerProducts(obj);
      if (obj.details?.length) {
        obj.details = await hydrateDetailsParentSnapshots(obj.details);
      }
      if (!obj.priceTypeName && obj.contactId && obj.lineName) {
        obj.priceTypeName = await fetchDefaultPriceTypeNameByContactId(obj.contactId, obj.lineName);
      }
      if (!obj.customerPriceTypeName && obj.customerCode && obj.lineName) {
        obj.customerPriceTypeName = await fetchDefaultCustomerPriceTypeName(obj.customerCode, obj.lineName);
      }
      setModel(obj);
      setIsLocked(response.data.data.locked);
      setCustomerCode(response.data.data.customerCode);
      setShow(true);
    } catch (error) {
      console.error(error);
      setIsLocked(true);
      setModel({error: error.message});
      toast.error(error.message);
    }
  };

  const getTitle = (model) => {
    var text = '';
    if (model.isDraft) text = "[Bản nháp] ";
    if (model.id) text += "Thông tin đơn hàng";
    if (!model.id) text += "Đơn hàng mới";

    return text;
  }

  const countOrderProducts = (details) => (details ?? []).filter((item) => (
    (item?.productId != null && item.productId !== '')
    || String(item?.productName ?? '').trim() !== ''
  )).length;

  /** Tải danh sách sản phẩm theo /api/products/getProductByCustomerCode (user vs admin trên BE). */
  const enrichModelWithCustomerProducts = async (m) => {
    const code = (m.customerCode ?? '').trim();
    if (!code) {
      return { ...m, customerProductList: [] };
    }
    try {
      const response = await axiosInstance.get('/api/products/getProductByCustomerCode', {
        params: { customerCode: code },
      });
      if (!response.data?.success) {
        toast.error(response.data?.message || 'Không tải được danh sách sản phẩm');
        return { ...m, customerProductList: [] };
      }
      return { ...m, customerProductList: response.data.data ?? [] };
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || error.message);
      return { ...m, customerProductList: [] };
    }
  };

  const fetchDefaultPriceTypeNameByContactId = async (contactId, lineName) => {
    if (!contactId || !lineName) return DEFAULT_PRICE_TYPE_NAME;

    try {
      const response = await axiosInstance.get('/api/contacts/getContact', {
        params: { id: contactId },
      });
      return resolveDefaultPriceTypeName(response.data?.data, lineName);
    } catch (error) {
      console.error(error);
      toast.error(error.message);
      return DEFAULT_PRICE_TYPE_NAME;
    }
  };

  const clearResolvedContact = (currentModel) => ({
    ...currentModel,
    contactId: null,
    contactCode: '',
    contactName: '',
    priceTypeName: '',
  });

  const applyDefaultCustomerPriceType = async (currentModel) => {
    const code = String(currentModel.customerCode ?? '').trim();
    const line = String(currentModel.lineName ?? '').trim();
    if (!code || !line) {
      return { ...currentModel, customerPriceTypeName: '' };
    }
    const customerPriceTypeName = await fetchDefaultCustomerPriceTypeName(code, line);
    return { ...currentModel, customerPriceTypeName };
  };

  const applyDefaultContactByCustomerAndLine = async (currentModel) => {
    const customerCode = String(currentModel.customerCode ?? '').trim();
    const lineName = String(currentModel.lineName ?? '').trim();

    if (!customerCode || !lineName) {
      return clearResolvedContact(currentModel);
    }

    try {
      const params = { customerCode, lineName };
      const response = await axiosInstance.get('/api/contacts/getDefaultContact', { params });
      const obj = response.data.data;

      if (!obj?.id) {
        return clearResolvedContact(currentModel);
      }

      return {
        ...currentModel,
        contactId: obj.id,
        contactCode: obj.code ?? '',
        contactName: obj.name ?? '',
        priceTypeName: resolveDefaultPriceTypeName(obj, lineName),
      };
    } catch (error) {
      console.error(error);
      toast.error(error.message);
      return currentModel;
    }
  };

  const normalizeSearchText = (value) => String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

  const filterProductPickerItems = (keyword, products) => {
    const seenIds = new Set();
    // BE gộp SP cá nhân + user_product nên có thể trùng id → trùng rowKey trong picker.
    const list = (Array.isArray(products) ? products : []).filter((p) => {
      if (p?.id == null || seenIds.has(p.id)) return false;
      seenIds.add(p.id);
      return true;
    });
    const q = normalizeSearchText(keyword);
    if (!q) return list;
    return list
      .filter((p) => {
        const name = normalizeSearchText(p?.name);
        const code = normalizeSearchText(p?.code);
        const matchedProperty = (p?.properties ?? []).some((prop) => {
          const propertyCode = normalizeSearchText(prop?.code);
          const propertyValue = normalizeSearchText(prop?.propertiesValue);
          return propertyCode.includes(q) || propertyValue.includes(q);
        });
        return name.includes(q) || code.includes(q) || matchedProperty;
      });
  };

  const normalizeProductName = (value) => normalizeSearchText(value);

  const findCustomerProductByName = (products, productName) => {
    const normalizedName = normalizeProductName(productName);
    if (!normalizedName) return null;
    return (Array.isArray(products) ? products : []).find(
      (product) => normalizeProductName(product?.name) === normalizedName,
    ) ?? null;
  };

  const getProductDetailCached = async (productId) => {
    if (!productId) return null;
    const cached = productDetailCacheRef.current.get(Number(productId));
    if (cached) return cached;
    const response = await axiosInstance.get('/api/products/getProductDetailById', {
      params: { id: productId },
    });
    const detail = response.data?.data ?? null;
    if (detail?.id != null) {
      productDetailCacheRef.current.set(Number(detail.id), detail);
    }
    return detail;
  };

  const buildProductPickerRows = async (keyword, products, currentDetail) => {
    const filteredProducts = filterProductPickerItems(keyword, products);
    const details = await Promise.all(
      filteredProducts.map(async (product) => ({
        product,
        detail: await getProductDetailCached(product.id),
      })),
    );

    return details.flatMap(({ product, detail }) => {
      const parent = detail ?? product;
      const parentSelected =
        currentDetail?.productId != null &&
        String(currentDetail.productId) === String(product.id) &&
        (currentDetail?.productPropertyId == null || currentDetail?.productPropertyId === '');

      const parentRow = {
        rowKey: `parent-${product.id}`,
        product,
        propertyId: null,
        imagePath: parent?.imagePath ?? product?.imagePath,
        code: parent?.code ?? product?.code,
        subCode: '',
        name: parent?.name ?? product?.name,
        subName: '',
        price: parent?.price ?? product?.price,
        weight: parent?.weight ?? product?.weight,
        dimensions: formatPropertyDimensions(parent),
        isCurrent: parentSelected,
      };

      const propertyRows = (detail?.properties ?? []).map((prop, index) => ({
        rowKey: `property-${product.id}-${prop.id ?? index}`,
        product,
        propertyId: prop.id ?? null,
        imagePath: prop.imagePath ?? detail?.imagePath ?? product?.imagePath,
        code: prop.code ?? detail?.code ?? product?.code,
        subCode: detail?.code ?? product?.code ?? '',
        name: detail?.name ?? product?.name ?? 'Không có tên',
        subName: prop.propertiesValue ?? 'Thuộc tính',
        price: prop.price ?? detail?.price ?? product?.price,
        weight: prop.weight ?? detail?.weight ?? product?.weight,
        dimensions: propertyRowDimensionsDisplay(prop, detail ?? product),
        isCurrent:
          currentDetail?.productId != null &&
          String(currentDetail.productId) === String(product.id) &&
          currentDetail?.productPropertyId != null &&
          String(currentDetail.productPropertyId) === String(prop.id),
      }));

      return [parentRow, ...propertyRows];
    });
  };

  const previewProductInModal = async (product) => {
    if (!product?.id) {
      setProductPickerModal((prev) => ({
        ...prev,
        previewProductId: null,
        previewProductDetail: null,
        loadingPreview: false,
      }));
      return;
    }

    const requestId = productPreviewRequestRef.current + 1;
    productPreviewRequestRef.current = requestId;
    setProductPickerModal((prev) => ({
      ...prev,
      previewProductId: product.id,
      loadingPreview: true,
    }));

    try {
      const detail = await getProductDetailCached(product.id);
      if (productPreviewRequestRef.current !== requestId) return;
      setProductPickerModal((prev) => ({
        ...prev,
        previewProductId: product.id,
        previewProductDetail: detail,
        loadingPreview: false,
      }));
    } catch (error) {
      console.error(error);
      if (productPreviewRequestRef.current !== requestId) return;
      toast.error(error.response?.data?.message || error.message);
      setProductPickerModal((prev) => ({
        ...prev,
        loadingPreview: false,
      }));
    }
  };

  const closeProductPickerModal = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    productPreviewRequestRef.current += 1;
    setProductPickerModal({
      show: false,
      detailIndex: -1,
      keyword: '',
      items: [],
      previewProductId: null,
      previewProductDetail: null,
      loadingList: false,
      loadingPreview: false,
      allowMultiple: false,
    });
  };

  const handleOpenProductPicker = async (detailIndex) => {
    const detail = model.details?.[detailIndex];
    const allowMultiple = detail?.productId == null || detail?.productId === '';
    await openProductPickerModal(detailIndex, '', allowMultiple);
  };

  const openProductPickerModal = async (detailIndex, keyword = '', allowMultiple = false) => {
    setProductPickerModal((prev) => ({
      ...prev,
      show: false,
      allowMultiple,
      detailIndex,
      keyword,
      items: [],
      loadingList: true,
      loadingPreview: false,
    }));

    const refreshed = await enrichModelWithCustomerProducts(model);
    setModel(refreshed);

    const currentDetail = refreshed.details?.[detailIndex];
    const items = await buildProductPickerRows(keyword, refreshed.customerProductList, currentDetail);

    setProductPickerModal((prev) => ({
      ...prev,
      show: items.length > 0,
      allowMultiple,
      detailIndex,
      keyword,
      items,
      loadingList: false,
      previewProductId: null,
      previewProductDetail: null,
    }));
  };

  const handleProductPickerKeywordChange = async (keyword) => {
    const requestId = productSearchRequestRef.current + 1;
    productSearchRequestRef.current = requestId;
    const currentDetail = model.details?.[productPickerModal.detailIndex];
    const items = await buildProductPickerRows(keyword, model.customerProductList, currentDetail);
    if (productSearchRequestRef.current !== requestId) return;
    setProductPickerModal((prev) => ({
      ...prev,
      keyword,
      items,
      loadingList: false,
    }));
  };

  const materializeOrderDetailFromProduct = (baseItem, productDetail, options = {}) => {
    const item = baseItem ?? {};
    var obj = { ...productDetail };
    obj.productId = obj.id;
    obj.productCode = obj.code;
    delete obj.id;
    obj.quantity = 1;
    obj.packageCount = 1;
    obj.money = obj.priceDeclare;

    var checkSize = obj.sizeX + obj.sizeY + obj.sizeZ;
    obj.size3 = (checkSize > 0);

    obj.providerName = obj.bestProviderName;
    obj.productLink = obj.bestProductLink;
    obj.productName = obj.name ?? item.productName;
    obj.parentProductSnapshot = buildParentSnapshotFromProductDto(obj);

    if (obj.properties?.length > 0) {
      const savedPid = options.propertyId ?? item.productPropertyId ?? obj.productPropertyId;
      if (savedPid != null && savedPid !== '') {
        const matchProp = obj.properties.find((pr) => pr.id === savedPid || String(pr.id) === String(savedPid));
        if (matchProp) {
          obj.productPropertyId = matchProp.id;
          applyProductPropertyToDetail(obj, matchProp);
        } else {
          obj.productPropertyId = null;
        }
      } else {
        obj.productPropertyId = null;
      }
    } else {
      obj.productPropertyId = null;
    }

    if (options.imagePath) {
      obj.imagePath = options.imagePath;
    }

    return { ...item, ...obj };
  };

  const loadSelectedProductToDetail = async (detailIndex, product, options = {}) => {
    if (!product?.id) return false;
    const newDetails = [...model.details];
    const item = newDetails[detailIndex];
    if (!item) return false;

    setIsLocked(true);
    try {
      const productDetail = options.productDetail ?? await getProductDetailCached(product.id);
      if (!productDetail) return false;
      newDetails[detailIndex] = materializeOrderDetailFromProduct(item, productDetail, options);
      setModel({ ...model, details: newDetails });
      return true;
    } catch (error) {
      console.error(error);
      toast.error(error.message);
      return false;
    } finally {
      setIsLocked(false);
    }
  };

  const chooseProductsFromPicker = async (rows) => {
    if (!rows?.length) return;
    const detailIndex = productPickerModal.detailIndex;
    setIsLocked(true);
    try {
      const details = [...(model.details ?? [])];
      const canReplace = detailIndex >= 0 && Boolean(details[detailIndex]);
      let replaced = false;
      const appended = [];
      for (const row of rows) {
        if (!row?.product?.id) continue;
        const productDetail = await getProductDetailCached(row.product.id);
        if (!productDetail) continue;
        const base = canReplace && !replaced ? { ...details[detailIndex] } : {};
        const built = materializeOrderDetailFromProduct(base, productDetail, {
          propertyId: row.propertyId,
          imagePath: row.imagePath,
        });
        if (canReplace && !replaced) {
          details[detailIndex] = built;
          replaced = true;
        } else {
          appended.push(built);
        }
      }
      if (!replaced && appended.length === 0) {
        toast.error('Không thêm được sản phẩm đã chọn.');
        return;
      }
      details.push(...appended);
      setModel({ ...model, details });
      closeProductPickerModal();
    } catch (error) {
      console.error(error);
      toast.error(error.message);
    } finally {
      setIsLocked(false);
    }
  };

  const chooseProductFromPicker = async (detailIndex, product, imagePath) => {
    if (!product) return;
    await loadSelectedProductToDetail(detailIndex, product, { imagePath });
    closeProductPickerModal();
  };

  const chooseProductPropertyFromModal = async (detailIndex, product, propertyId, imagePath) => {
    if (!product) return;
    await loadSelectedProductToDetail(detailIndex, product, {
      productDetail: productPickerModal.previewProductDetail,
      propertyId,
      imagePath,
    });
    closeProductPickerModal();
  };

  /** Snapshot sản phẩm cha (không lưu DB — chỉ dùng UI). */
  const buildParentSnapshotFromProductDto = (p) => ({
    productCode: p.code,
    price: p.price,
    priceDeclare: p.priceDeclare,
    weight: p.weight,
    size: p.size,
    sizeX: p.sizeX,
    sizeY: p.sizeY,
    sizeZ: p.sizeZ,
    taxCode: p.taxCode,
    taxImport: p.taxImport,
    taxVat: p.taxVat,
    imagePath: p.imagePath,
    description: p.description,
    noteUser: p.note ?? '',
    size3: ((p.sizeX ?? 0) + (p.sizeY ?? 0) + (p.sizeZ ?? 0)) > 0,
  });

  /** Sau getOrder: backend không trả parentProductSnapshot — bơm từ SP gốc để dòng “mặc định” không bị “—”. */
  const hydrateDetailsParentSnapshots = async (details) => {
    if (!details?.length) return details;
    const list = details.map((d) => ({ ...d }));
    const ids = [...new Set(list.map((d) => d.productId).filter((id) => id != null))];
    if (!ids.length) return list;
    const cache = new Map();
    await Promise.all(
      ids.map(async (pid) => {
        try {
          const p = await getProductDetailCached(pid);
          if (p?.id != null) cache.set(Number(pid), p);
        } catch (e) {
          console.error(e);
        }
      }),
    );
    for (const d of list) {
      if (d.productId == null || !cache.has(Number(d.productId))) continue;
      const p = cache.get(Number(d.productId));
      if (!d.parentProductSnapshot) {
        d.parentProductSnapshot = buildParentSnapshotFromProductDto(p);
      }
      if (!d.properties || d.properties.length === 0) {
        d.properties = p.properties ?? [];
      }
    }
    return list;
  };

  const setModelItem = async (name, e) => {
    if (readOnly) return;
    var value = e.target.value;
    var newModel = { ...model };
    newModel[name] = value;
    if (name === 'contactCode') {
      var thTag = e.target.closest('.th');
      if (thTag) {
        var selecteTag = thTag.querySelector('.item_selected');
        if (selecteTag && selecteTag.innerText === value && value) {
          newModel.contactId = e.target.dataset.text;
          newModel.priceTypeName = await fetchDefaultPriceTypeNameByContactId(newModel.contactId, newModel.lineName);
        }
      }
    }
    if (name === 'customerCode') {
      var customerThTag = e.target.closest('.th');
      var selectedCustomerTag = customerThTag?.querySelector('.item_selected');
      const isConfirmedCustomerCode = selectedCustomerTag && selectedCustomerTag.innerText === value && value;

      if (isConfirmedCustomerCode) {
        newModel = await applyDefaultContactByCustomerAndLine(newModel);
        newModel = await applyDefaultCustomerPriceType(newModel);
      } else {
        newModel = clearResolvedContact(newModel);
        newModel.customerPriceTypeName = '';
      }
    } else if (name === 'lineName') {
      newModel = await applyDefaultContactByCustomerAndLine(newModel);
      newModel = await applyDefaultCustomerPriceType(newModel);
    }

    if (name === 'customerCode') {
      newModel = await enrichModelWithCustomerProducts(newModel);
    }

    setModel(newModel);
  }

  const formatPropertyDimensions = (row) => {
    if (!row) return '';
    const x = Number(row.sizeX ?? 0);
    const y = Number(row.sizeY ?? 0);
    const z = Number(row.sizeZ ?? 0);
    if (x + y + z > 0) return `${x}×${y}×${z}`;
    if (row.size != null && row.size !== '') return `${row.size} m³`;
    return '';
  };

  /** KL: variant không nhập kg thì hiển thị kg sản phẩm cha. TT/KT: tương tự khi variant không có kích thước. */
  const propertyRowWeightDisplay = (prop, parentSpec) => {
    if (prop?.weight != null && prop.weight !== '') return prop.weight;
    if (parentSpec?.weight != null && parentSpec.weight !== '') return parentSpec.weight;
    return '—';
  };

  const propertyRowDimensionsDisplay = (prop, parentSpec) => {
    const own = formatPropertyDimensions(prop);
    if (own) return own;
    return (parentSpec && formatPropertyDimensions(parentSpec)) || '—';
  };

  /** Khôi phục giá trị sản phẩm cha (không thuộc tính con). */
  const applyParentProductDefaults = (detail) => {
    if (!detail) return;
    const p = detail.parentProductSnapshot;
    if (!p) {
      detail.productPropertyId = null;
      return;
    }
    detail.productPropertyId = null;
    detail.productCode = p.productCode ?? detail.productCode;
    detail.price = p.price;
    detail.priceDeclare = p.priceDeclare;
    detail.weight = p.weight;
    detail.size = p.size;
    detail.sizeX = p.sizeX;
    detail.sizeY = p.sizeY;
    detail.sizeZ = p.sizeZ;
    detail.taxCode = p.taxCode;
    detail.taxImport = p.taxImport;
    detail.taxVat = p.taxVat;
    detail.imagePath = p.imagePath;
    detail.description = p.description ?? detail.description;
    detail.noteUser = p.noteUser ?? detail.noteUser;
    detail.size3 = !!p.size3;
    const quantity = detail.quantity ?? 1;
    detail.money = quantity * (detail.priceDeclare ?? 0);
    detail.moneyIn = quantity * (detail.priceIn ?? 0);
  };

  const getSelectedPropertyLabel = (item) => {
    if (item?.productPropertyId == null || item.productPropertyId === '') return '';
    const prop = (item.properties ?? []).find((p) => String(p.id) === String(item.productPropertyId));
    return String(prop?.propertiesValue ?? '').trim();
  };

  const applyProductPropertyToDetail = (detail, property) => {
    if (!detail || !property) return detail;

    detail.productPropertyId = property.id ?? detail.productPropertyId ?? null;
    detail.productCode = property.code ?? detail.productCode;
    detail.price = property.price ?? detail.price;
    detail.priceDeclare = property.priceDeclare ?? detail.priceDeclare;
    detail.weight = property.weight ?? detail.weight;
    detail.size = property.size ?? detail.size;
    detail.sizeX = property.sizeX ?? detail.sizeX;
    detail.sizeY = property.sizeY ?? detail.sizeY;
    detail.sizeZ = property.sizeZ ?? detail.sizeZ;
    detail.taxCode = property.taxCode ?? detail.taxCode;
    detail.taxImport = property.taxImport ?? detail.taxImport;
    detail.taxVat = property.taxVat ?? detail.taxVat;
    detail.imagePath = property.imagePath ?? detail.imagePath;
    detail.description = property.description || detail.description;
    detail.noteUser = property.note || detail.noteUser;

    var quantity = detail.quantity ?? 1;
    detail.money = quantity * (detail.priceDeclare ?? 0);
    detail.moneyIn = quantity * (detail.priceIn ?? 0);

    var checkSize = (detail.sizeX ?? 0) + (detail.sizeY ?? 0) + (detail.sizeZ ?? 0);
    detail.size3 = (checkSize > 0);

    return detail;
  };

  /** Hiển thị dòng “sản phẩm mặc định” trong bảng thuộc tính (snapshot hoặc dòng hiện tại). */
  const getParentPropertyDisplay = (item) => {
    const p = item.parentProductSnapshot;
    if (p) return p;
    if (item.productPropertyId == null || item.productPropertyId === '') {
      return {
        productCode: item.productCode,
        price: item.price,
        priceDeclare: item.priceDeclare,
        weight: item.weight,
        size: item.size,
        sizeX: item.sizeX,
        sizeY: item.sizeY,
        sizeZ: item.sizeZ,
        taxCode: item.taxCode,
        taxImport: item.taxImport,
        taxVat: item.taxVat,
        description: item.description,
        noteUser: item.noteUser,
      };
    }
    return null;
  };

  const selectOrderDetailProperty = (detailIndex, propertyId) => {
    const newDetails = [...model.details];
    const detail = newDetails[detailIndex];
    if (!detail?.properties?.length) return;
    if (propertyId == null || propertyId === '') {
      applyParentProductDefaults(detail);
    } else {
      const prop = detail.properties.find(
        (pr) => pr.id === propertyId || String(pr.id) === String(propertyId),
      );
      if (prop) applyProductPropertyToDetail(detail, prop);
    }
    setModel({ ...model, details: newDetails });
  };

  const setDetailsItem = async (index, name, e) => {
    if (readOnly) return;
    if (supplierDisabled && (name === 'providerName' || name === 'priceIn')) return;
    var value = e.target.value;
    if (value === 'on') value = e.target.checked;
    const newDetails = [...model.details];
    var item = newDetails[index];
    if (name === 'price') {
      item.money = item.quantity * value;
    }
    if (name === 'priceIn') {
      item.moneyIn = item.quantity * value
    }
    if (name === 'quantity') {
      item.money = value * item.priceDeclare;
      item.moneyIn = value * item.priceIn;
    }
    if (name === 'size') {
      item.sizeX = '';
      item.sizeY = '';
      item.sizeZ = '';
    }
    if (name === 'productName') {
      const matchedProduct = findCustomerProductByName(model.customerProductList, value);
      if (matchedProduct) {
        try {
          var productId = resolveOrderProductId(model.customerProductList, matchedProduct.name);
          if (!productId) {
            toast.error('Không xác định được sản phẩm. Vui lòng chọn từ danh sách gợi ý.');
          } else {
            await loadSelectedProductToDetail(index, { ...matchedProduct, id: productId });
            closeProductPickerModal();
            return;
          }
        } catch (error) {
          console.error(error);
          toast.error(error.message);
        }
      }
    }
    if (name === 'providerName') {
      var thTag = e.target.closest('.th');
      if (thTag) {
        var selecteTag = thTag.querySelector('.item_selected');
        if (selecteTag && selecteTag.innerText === value && value) {
          setIsLocked(true);
          try {
            const params = {
              refer: value,
              referId: item.productId,
            };
            const response = await axiosInstance.get(`/api/providers/getProductProviderByName`,{ params });

            var obj = response.data.data;
            console.log(obj);
            //item.priceIn = obj.price;
            item.productLink = obj.productLink;
          } catch (error) {
            console.error(error);
            toast.error(error.message);
          }
          setIsLocked(false);
        }
      }

    }
    if (name !== 'productPropertyId') {
      item[name] = value;
    }
    setModel({ ...model, details: newDetails });
    //updateWarningMessage({ ...model, details: newDetails });
  }

  const productCount = countOrderProducts(model.details);

  return (
    <Modal show={show} onHide={handleModalClose} size="xl">
      <div className="modal-dialog11 modal-lg11 position-relative">
        <form id="order_editor_form" className="form_validate normal-form" autoComplete="off" onSubmit={handleSubmitForm}>
          <div className="modal-content">
            <div className="modal-header position-relative flex-between">
              {model.orderCode && (<span className="record_id">Code: {model.orderCode}</span>)}
              <div>
                <h5 className="modal-title">
                  {getTitle(model)}
                  {isMuaHoOrder && productCount > 0 && (
                    <span className="ms-2 fw-normal text-muted">({productCount} sản phẩm)</span>
                  )}
                </h5>
                {model.error && (
                  <div className="error-msg-server">{model.error}</div>
                )}
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={handleModalClose}
                aria-label="Đóng"
              ></button>
            </div>
            <div className="modal-body mt-2">
              {reconcileWarning ? (
                <div className="alert alert-warning py-2 mb-2 d-flex align-items-start gap-2">
                  <FaExclamationTriangle className="text-danger flex-shrink-0 mt-1" aria-hidden />
                  <span>{reconcileWarning}</span>
                </div>
              ) : model.id && Number(model.statusRateId) === ORDER_STATUS_RATE_VAO_PHIEU && (
                <div className="alert alert-info py-2 mb-2 d-flex align-items-start gap-2">
                  <FaExclamationTriangle className="text-warning flex-shrink-0 mt-1" aria-hidden />
                  <span>Đơn đã vào phiếu đối soát. Sửa cân nặng, kích thước hoặc phí sẽ làm lệch số tiền đối soát ban đầu.</span>
                </div>
              )}
              <div className="group-box p-0" data-text="Thông tin chung 11">
                <div className="group-body">

                  <div className="row">
                    <div className="col-md-2 mb-3">
                      <label className="form-label required">Line</label>
                      <div className="th filter-editor">
                        <span className="filter-ib" data-source-key="editor_lines">
                        <input type="text" name="line" placeholder="Nhập line" data-required="Phải nhập mục này." disabled={inputDisabled} value={model.lineName ?? ''} onChange={(e) => setModelItem('lineName', e)} />
                        </span>
                      </div>
                    </div>
                    {model.isAdmin && (
                      <div className="col-md-2 mb-3">
                        {model.id && customerCode && !customerCode.includes('ZTO-VH') && (
                          <div>
                            <label className="form-label">&nbsp;</label>
                            <div>
                              <span>Mã khách hàng: </span>
                              <b>{model.customerCode ?? ''}</b>
                            </div>
                          </div>
                        )}
                        {(!model.id || (customerCode && customerCode.includes('ZTO-VH'))) && (
                          <div>
                            <label className="form-label required">Mã khách hàng</label>
                            <div className="th filter-editor">
                              <span className="filter-ib" data-source-url={API_URL_All + '/api/users/searchUserCodes'}>
                                <input type="text" name="customerCode" placeholder="Nhập mã khách hàng..." data-required="Phải nhập mục này." disabled={inputDisabled || model.locked} value={model.customerCode ?? ''} onChange={(e) => setModelItem('customerCode', e)} />
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {model.isAdmin && (
                      <div className="col-md-2 mb-3">
                        <label className="form-label">Bảng giá khách hàng</label>
                        <div className="th filter-editor">
                          <span className="filter-ib" data-source-url={API_URL_All + '/api/levels/searchCustomerPriceTypes'}>
                            <input
                              type="text"
                              placeholder="Nhập tên bảng giá khách hàng"
                              disabled={inputDisabled || model.locked || !String(model.customerCode ?? '').trim() || !String(model.lineName ?? '').trim()}
                              data-refer={`${model.lineName ?? ''}#${model.customerCode ?? ''}`}
                              value={model.customerPriceTypeName ?? ''}
                              onChange={(e) => setModel({ ...model, customerPriceTypeName: e.target.value })}
                            />
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="col-md-1 mb-3">
                      <label className="form-label">&nbsp;</label>
                      <div>
                        <CheckingTicket checkingId={model.statusRateId} checkingName={model.statusRateName} />
                      </div>
                    </div>
                    {model.id > 0 && model.userRank > 0 && (
                      <div className="col-md-3 rounded">
                        <RankDiscountDetails rank={model.userRank} rankDiscountPercent={model.rankDiscountPercent} feeOriginal={model.feeOriginal} fee={model.fee} />
                      </div>
                    )}  

                  </div>
                  <div className="row">
                    <div className="col-md-2 mb-3">
                      <label className="form-label">Mã vận đơn TQ</label>
                      <div className="">
                        <input type="text" className="form-control" placeholder="Mã vận đơn TQ" name="cnShippingCode" disabled={inputDisabled} value={model.cnShippingCode ?? ''} onChange={(e) => setModel({ ...model, cnShippingCode: e.target.value })} />
                      </div>
                    </div>

                    <div className="col-md-2 mb-3">
                      <label className="form-label">Mã vận đơn VN</label>
                      <div className="">
                        <input type="text" name="vnShippingCode" className="form-control" placeholder="Mã vận đơn VN" disabled={inputDisabled || model.locked} value={model.vnShippingCode ?? ''} onChange={(e) => setModel({ ...model, vnShippingCode: e.target.value })} />
                      </div>
                    </div>
                    <div className="col-md-8 mb-1 pe-0">
                      <label className="form-label">Ghi chú</label>
                      {model.isAdmin && (
                        <div className="">
                          <div>
                            <input type="text" className="form-control" placeholder="Ghi chú" disabled={inputDisabled} value={model.noteAdmin ?? ''} onChange={(e) => setModel({ ...model, noteAdmin: e.target.value })} />

                          </div>
                          {model.noteUser && (
                            <div className="">
                              <span>Lưu ý: </span>
                              <b>{model.noteUser}</b>
                            </div>
                          )}
                        </div>
                      )}
                      {!model.isAdmin && (
                        <div className="">
                          <div>
                            <input type="text" className="form-control" placeholder="Ghi chú" disabled={inputDisabled} value={model.noteUser ?? ''} onChange={(e) => setModel({ ...model, noteUser: e.target.value })} />

                          </div>
                          {model.noteAdmin && (
                            <div className="">
                              <span>Lưu ý: </span>
                              <b>{model.noteAdmin}</b>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-4 pe-0 mb-3">
                      <label className="form-label required">Tên đơn hàng</label>
                      <div className="">
                        <input type="text" name="name" className="form-control" data-required="Phải nhập mục này" placeholder="Tên đơn hàng" disabled={readOnly} value={model.name ?? ''} onChange={(e) => setModel({ ...model, name: e.target.value })} />
                      </div>
                    </div>

                    <div className="col-6 col-md-1 mb-3 p-0">
                      <label className="form-label">Số kiện</label>
                      <div className="">
                        <input type="number" className="form-control" placeholder="Số kiện" name="packageNumbers" disabled={inputDisabled} value={model.packageNumbers ?? ''} onChange={(e) => setModel({ ...model, packageNumbers: e.target.value })} />
                      </div>
                    </div>
                    <div className="col-6 col-md-1 mb-3 p-0">
                      <label className="form-label" data-note="kg">K.lượng</label>
                      <div className="">
                        <input type="number" className="form-control" placeholder="kg" name="packageUnitValue" disabled={inputDisabled} value={model.packageUnitValue ?? ''} onChange={(e) => setModel({ ...model, packageUnitValue: e.target.value })} />
                      </div>
                    </div>
                    <div className="col-12 col-md-3 mb-3 p-0">
                      <div className="size-options lable-size">
                        <ul>
                          <li data-for="size" className={model.size3 ? '' : 'active'}>
                            <span className="form-label" data-note="m3">Thể tích</span>
                          </li>
                          <li data-for="size3" className={model.size3 ? 'active' : ''}>
                            <span className="form-label" data-note="dài x rộng x cao (cm)">Kích thước</span>
                          </li>
                        </ul>
                        <i className="fa-solid fa-caret-down"></i>
                      </div>
                      <label className="form-label">&nbsp;</label>
                      <div id="size" className={model.size3 ? 'd-none' : ''}>
                        <input type="number" name="size" className="form-control" placeholder="m3" disabled={inputDisabled} value={model.packageUnitM3 ?? ''} onChange={(e) => setSizeItem('packageUnitM3', e)} />
                      </div>
                      <div id="size3" className={model.size3 ? 'size-3' : 'size-3 d-none'}>
                        <input type="number" className="form-control" placeholder="dài cm" disabled={inputDisabled} value={model.sizeX ?? ''} onChange={(e) => setModel({ ...model, sizeX: e.target.value })} />
                        <input type="number" className="form-control" placeholder="rộng cm" disabled={inputDisabled} value={model.sizeY ?? ''} onChange={(e) => setModel({ ...model, sizeY: e.target.value })} />
                        <input type="number" className="form-control" placeholder="cao cm" disabled={inputDisabled} value={model.sizeZ ?? ''} onChange={(e) => setModel({ ...model, sizeZ: e.target.value })} />
                      </div>
                    </div>
                    <div className="col-6 col-md-2 mb-3 p-0">
                      <label className="form-label" data-note="vnđ">Giá b.hiểm</label>
                      <div className="">
                        <input type="number" className="form-control form-price" placeholder="Giá bảo hiểm" disabled={inputDisabled} value={model.insurancePrice ?? ''} onChange={(e) => setModel({ ...model, insurancePrice: e.target.value })} />
                      </div>
                    </div>

                    <div className="col-6 col-md-1 mb-3 p-0">
                      <label className="form-label" data-note="vnđ">Phụ phí</label>
                      <div className="">
                        <input type="number" className="form-control form-price" placeholder="Phụ phí" disabled={inputDisabled || model.locked || !model.isAdmin} value={model.supplementaryFee ?? ''} onChange={(e) => setModel({ ...model, supplementaryFee: e.target.value })} />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-12 mb-3 pe-0">
                      <label className="form-label">Phương thức lấy hàng</label>
                      <div className="th filter-editor">
                        <span className="filter-ib" data-source-url={API_URL_All + '/api/import-orders/searchAddress'}><input type="text" placeholder="Nhập địa chỉ..." disabled={inputDisabled} data-refer={model.customerCode} value={model.shippingMethod ?? ''} onChange={(e) => setModel({ ...model, shippingMethod: e.target.value })} /></span>
                      </div>
                    </div>
                  </div>

                  {model.isAdmin && (
                    <div className="row">
                      <div className="col-md-3 mb-3">
                        <label className="form-label">Lưu trình</label>
                        <div className="th filter-editor">
                          <span className="filter-ib" data-source-key="list_status">
                            <input type="text" name="line" placeholder="Nhập lưu trình" disabled={inputDisabled || model.locked || model.isDraft} value={model.statusName ?? ''} onChange={(e) => setModel({ ...model, statusName: e.target.value })} />
                          </span>
                        </div>
                      </div>
                      {model.id && (
                        <div className="col-md-6 mb-3">
                          <label className="form-label">&nbsp;</label>
                          <div className="th filter-check">
                            <span className="filter-ib"><input type="checkbox" checked={model.locked} onChange={(e) => setModel({ ...model, locked: e.target.checked })} /></span>
                            <label className="form-check-label">Khóa đơn hàng</label>
                          </div>
                        </div>
                      )}

                      <div className="col-md-3 mb-3 d-none">
                        <label className="form-label">Loại đơn</label>
                        <div className="th filter-editor">
                          <span className="filter-ib" data-source-key="list_orderTypes">
                            <input type="text" placeholder="..." disabled={inputDisabled || model.locked} value={model.draftText ?? ''} onChange={(e) => setModel({ ...model, draftText: e.target.value })} />
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {!model.isAdmin && (
                    <div className="row">
                      <div className="col-md-3 mb-3">
                        <label className="form-label">Lưu trình: </label>
                        <span className="select3-item ms-3">{model.statusName ?? ''}</span>
                      </div>
                    </div>
                  )}
                </div>
                {model.isAdmin && (
                  <div>
                    <div className="area-provider">
                      {showOrderSupplierSection && requireMuaHoSupplier && (
                        <div className="row m-0 product_provider">
                          <div className="col-md-6 mb-3">
                            <label className="form-label">
                              <span title={`careId: ${model.careId??''}`}>Nhà cung cấp </span>                            
                            </label>
                            <div className="th filter-editor">
                              <span className="filter-ib" data-source-url={API_URL_All + '/api/providers/searchProviderNamesByCustomerCode'}>
                                <input type="text" name="providerName" disabled={supplierDisabled || model.locking || model.locked} placeholder="Nhập tên nhà cung cấp..." data-refer={`${model.customerCode}`} {...(requireMuaHoSupplier && !supplierDisabled ? { 'data-required': 'Đơn mua hộ bắt buộc chọn nhà cung cấp.' } : {})} value={model.providerName ?? ''} onChange={(e) => { if (!supplierDisabled) setModel({ ...model, providerName: e.target.value }); }} />
                              </span>
                            </div>
                          </div>
                          <div className="col-1 col-md-1 mb-3 p-0">
                            <label className="form-label">&nbsp;</label>
                            <div className="p-1">
                              {model.link && model.link.length > 2 && (<a href={model.link} target="_black">Link NCC</a>)}
                            </div>
                          </div>
                          <div className="col-1 col-md-2 mb-3 p-0">
                            <label className="form-label">&nbsp;</label>
                            <div className="flex-end">
                              {model.providerId && (<FeedbackChecking commentId={model.commentId} />)}
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="row m-0">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            <span>Đối tác </span>
                          </label>
                          <div>
                            <input type="text" className="form-control d-none" placeholder="..." disabled={true} value={model.contactName ?? ''} />

                            <div className="th filter-editor">
                              <span className="filter-ib" data-source-key="code" data-source-text="id" data-source-url={API_URL_All + '/api/contacts/searchContactNames'}><input type="text" data-refer={model.lineName} placeholder="Nhập tên đối tác..." value={model.contactCode ?? ''} onChange={(e) => setModelItem('contactCode', e)} /></span>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-2 mb-3 p-0">
                          <label className="form-label">
                            <span>Bảng giá</span>
                          </label>
                          <div className="th filter-editor">
                            <span className="filter-ib" data-source-url={API_URL_All + '/api/contacts/searchContactPriceTypes'}>
                              <input type="text" placeholder="Nhập tên bảng giá" data-refer={`${model.lineName}#${model.contactId}`} value={model.priceTypeName ?? ''} onChange={(e) => setModel({ ...model, priceTypeName: e.target.value })} />
                            </span>
                          </div>
                        </div>
                        <div className="col-6 col-md-2 mb-3 p-0">
                          <label className="form-label" data-note="vnđ">Phụ phí đối tác</label>
                          <div className="">
                            <input type="number" className="form-control form-price" placeholder="Phụ phí" disabled={inputDisabled || model.locked || !model.isAdmin} value={model.contactFeeSurcharge ?? ''} onChange={(e) => setModel({ ...model, contactFeeSurcharge: e.target.value })} />
                          </div>
                        </div>
                        <div className="col-6 col-md-2 mb-3 p-0">
                          <label className="form-label">Mã bao</label>
                          <div className="">
                            <input type="text" className="form-control form-price" disabled={inputDisabled || model.locking} placeholder="Nhập mã bao" value={model.sackCode ?? ''} onChange={(e) => setModel({ ...model, sackCode: e.target.value })} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {isMuaHoOrder && model.details && model.details.map((item, index) => (
                <div className="group-box p-0 order-product-panel" key={index} data-text={`Sản phẩm ${index + 1}`}>
                  {model.details.length >= 1 && !model.locked && !readOnly && (model.id || requireMuaHoSupplier) && (
                    <span className="close-i close-group" onClick={() => handleRemoveItem(index)}></span>
                  )}

                  <div className="order-product-panel-img">
                    {item.imagePath ? (
                      <img src={buildProductImageUrl(item.imagePath)} alt={item.productName || 'Sản phẩm'} />
                    ) : null}
                  </div>

                  <div className="order-product-panel-content">
                    <div className="group-body">
                      <div className="row">
                        <div className="col-md-3 mb-3 p-0">
                          <label className="form-label required">Tên sản phẩm</label>
                          <div className="position-relative">
                              <input
                                type="text"
                                className="form-control"
                                id={`order-editor-product-name-${index}`}
                                autoComplete="off"
                                disabled={inputDisabled}
                                data-refer={`${model.customerCode}---${model.providerName}`}
                                placeholder="Chọn sản phẩm..."
                                data-required="Phải nhập mục này."
                                name="name"
                                value={item.productName ?? ''}
                                onChange={(e) => setDetailsItem(index, 'productName', e)}
                                onClick={() => handleOpenProductPicker(index)}
                              />
                              {getSelectedPropertyLabel(item) && (
                                <div className="small fst-italic text-truncate mt-1" title={getSelectedPropertyLabel(item)}>
                                  PL: {getSelectedPropertyLabel(item)}
                                </div>
                              )}
                            </div>
                          </div>
                        <div className="col-6 col-md-2 mb-3 p-0">
                          <label className="form-label" data-note="vnđ">Giá nhập</label>
                          <div className="">
                            <input type="number" className="form-control" placeholder="Giá nhập" disabled={inputDisabled} value={item.price ?? ''} onChange={(e) => setDetailsItem(index, 'price', e)} />
                          </div>
                        </div>
                        <div className="col-6 col-md-1 mb-3 p-0">
                          <label className="form-label">S.lượng</label>
                          <div className="">
                            <input type="number" className="form-control" placeholder="S.lượng" disabled={inputDisabled} value={item.quantity ?? ''} onChange={(e) => setDetailsItem(index, 'quantity', e)} />
                          </div>
                        </div>
                        <div className="col-6 col-md-2 mb-3 p-0">
                          <label className="form-label" data-note="vnđ">Thành tiền</label>
                          <div className="">
                            <input type="text" className="form-control form-price" placeholder="Thành tiền" disabled={true} value={general.formatNumberFull(item.money)} />
                          </div>
                        </div>
                        {!model.isAdmin && (
                          <div className="col-12 col-md mb-3 p-0">
                            <label className="form-label">Ghi chú sản phẩm</label>
                            <div className="">
                              <input type="text" className="form-control" placeholder="Ghi chú" disabled={inputDisabled || item.locking || item.locked || model.locked} value={item.noteUser ?? ''} onChange={(e) => setDetailsItem(index, 'noteUser', e)} />
                            </div>
                            {item.noteAdmin && (
                              <div className="">
                                <span>Lưu ý: </span>
                                <b>{item.noteAdmin}</b>
                              </div>
                            )}
                          </div>
                        )}
                        {model.isAdmin && (
                          <div className="col-12 col-md mb-3 p-0">
                            <label className="form-label">Ghi chú sản phẩm</label>
                            <div className="">
                              <input type="text" className="form-control" placeholder="Ghi chú" disabled={true} value={item.noteUser ?? ''} />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {model.isAdmin && (
                      <div>
                        <div className="area-provider">
                          {showOrderSupplierSection && (
                            <div className="product_provider">
                              <div className="row m-0">
                                <div className="col-md-4 mb-3 p-0">
                                  <div>
                                    <label className="form-label" title={`careId: ${model.careId ?? ''}`}>Link sản phẩm</label>
                                    <div className="">
                                      {item.productLink && item.productLink.length > 20 && (
                                        <a href={item.productLink} target="_black">{item.productName}</a>
                                      )}
                                      {(!item.productLink || item.productLink.length <= 20) && (
                                        <span >Không có link</span>
                                      )}
                                    </div>
                                  </div>

                                </div>
                                <div className="col-6 col-md-2 mb-3 p-0">
                                  <label className="form-label" data-note="CNY">Giá NCC</label>
                                  <div className="">
                                    <input type="text" className="form-control form-price" placeholder="Giá NCC" disabled={supplierDisabled || item.locking || item.locked || model.locked} onChange={(e) => setDetailsItem(index, 'priceIn', e)} value={item.priceIn ?? ''} />
                                  </div>
                                </div>
                                <div className="col-2 mb-3 p-0">
                                  <label className="form-label" data-note="CNY">Thành tiền NCC</label>
                                  <div className="">
                                    <input type="text" className="form-control form-price" placeholder="Thành tiền NCC" disabled={true} value={general.formatNumberFull(item.moneyIn)} />
                                  </div>
                                </div>

                                <div className="col-12 col-md mb-3 p-0">
                                  <label className="form-label">Ghi chú sản phẩm</label>
                                  <div className="">
                                    <input type="text" className="form-control" placeholder="Ghi chú" disabled={supplierDisabled || item.locking || item.locked || model.locked} value={item.noteAdmin ?? ''} onChange={(e) => setDetailsItem(index, 'noteAdmin', e)} />
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                </div>
              ))}
              {isMuaHoOrder && !readOnly && !model.locked && (
                <button type="button" className="small-btn d-inline-flex align-items-center gap-1" onClick={addOrderProduct}>
                  <FaPlus size={12} aria-hidden />
                  Thêm sản phẩm
                </button>
              )}
              <div>
                {/* {model.id > 0 && model.isAdmin && (
                  <div>
                    <span>Xếp hạng: {model.userRank}</span>
                    <span> - Hàng nặng: {general.formatNumber(model.feeWeightBase)}/kg </span>
                    <span> - Cồng kềnh: {general.formatNumber(model.feeSizeBase)}/m3 </span>
                    <span> - Phí đơn hàng: {general.formatNumber(model.fee)} vnđ </span>
                  </div>
                )} */}             
              </div>
            </div>
            <RecordCreatedMeta record={model} />
            <div className="modal-footer justify-content-between align-items-center">
              <div>
                {model.id && canDeleteOrder && (readOnly || !isLocked) && (
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <button type="button" className="btn-small d-inline-flex align-items-center gap-1" onClick={() => handleDeletOrder()}>
                      <FaTrash size={13} aria-hidden />
                      Xóa
                    </button>
                    {!readOnly && !isLocked && model.isDraft && (
                      <button type="button" className="btn-small d-inline-flex align-items-center gap-1" onClick={() => handleTransformOrder()}>
                        <FaArrowCircleRight size={13} aria-hidden />
                        Chuyển đơn chuẩn
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div>
                {!readOnly ? (
                  <button type="submit" className="btn-sm btn btn-primary d-inline-flex align-items-center gap-1">
                    <FaSave size={14} aria-hidden />
                    {model.isDraft ? 'Lưu đơn nháp' : 'Lưu đơn'}
                  </button>
                ) : (
                  <button type="button" className="btn-sm btn btn-secondary d-inline-flex align-items-center gap-1" onClick={handleModalClose}>
                    <FaTimes size={14} aria-hidden />
                    Đóng
                  </button>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
      <OrderProductPickerModal
        show={productPickerModal.show}
        keyword={productPickerModal.keyword}
        customerCode={model.customerCode}
        rows={productPickerModal.items}
        loadingList={productPickerModal.loadingList}
        locked={isLocked || readOnly}
        allowMultiple={productPickerModal.allowMultiple}
        onClose={closeProductPickerModal}
        onSearch={handleProductPickerKeywordChange}
        onChooseRows={chooseProductsFromPicker}
        onChooseRow={(row) => {
          if (row.propertyId != null) {
            chooseProductPropertyFromModal(productPickerModal.detailIndex, row.product, row.propertyId, row.imagePath);
            return;
          }
          chooseProductFromPicker(productPickerModal.detailIndex, row.product, row.imagePath);
        }}
      />
    </Modal>
  );
};

export default OrderEditor;
